import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

/**
 * Bronson Checkbox component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-checkbox.html
 *
 * @param {string} [id] - Identifier to link between the `<label>` and `<input>`.
 * @param {boolean} [error] - Mark the Checkbox as invalid.
 * @param {string} [className] - Additional modifiers on the `<label>` element.
 * @param {string|React.ReactNode} [children] - The {@link Checkbox} label.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the `<input>` element. Replaces `checkbox-attribute` from Bronson template.
 * @returns {JSX.Element} - The {@link Checkbox} component.
 * @constructor
 */
export function Checkbox({ id, error, children, testId, className, ...otherProps }) {
  const labelClassNameList = classNames('c-checkbox', className).trim()

  const inputClassNameList = classNames('c-checkbox__input', {
    'is-error ': error,
  }).trim()

  return (
    <label className={labelClassNameList} htmlFor={id}>
      <input
        {...otherProps}
        data-testid={testId}
        className={inputClassNameList}
        type="checkbox"
        id={id}
        {...otherProps}
      />
      <span className="c-checkbox__label">{children}</span>
    </label>
  )
}

Checkbox.propTypes = {
  id: PropTypes.string, // Bronson template: 'id'.
  error: PropTypes.bool, // Bronson template: 'checkbox-is-error'.
  className: PropTypes.string, // Custom CSS classes on `.c-checkbox`. Bronson template: 'modifiers'.
  children: PropTypes.node, // Bronson template: 'checkbox-label'.
  testId: PropTypes.string, // Added for `[data-testid]` attribute.
}
